// Crafting Integration Service - Import data from GoodWork Crafting app
// Both apps share the same Firebase project (goodwork-crafting-app)

import { doc, getDoc, Timestamp } from '@firebase/firestore';
import { db } from './firebase';
import type { CraftingData } from '../types';

// GoodWork Crafting uses 'userProfiles' collection (primary)
// Fallback to 'users' for legacy data
const CRAFTING_USERS_COLLECTION = 'userProfiles';
const CRAFTING_LEGACY_COLLECTION = 'users';

// Valou Areas IDs
const VALOU_AREA_IDS = [
  'persoenlichkeitSkills',
  'taetigkeit',
  'privatesLeben',
  'stilWirkung',
  'ressourcenMittel',
  'gesundheit'
];

const VALOU_AREA_NAMES: Record<string, string> = {
  'persoenlichkeitSkills': 'Persoenlichkeit & Skills',
  'taetigkeit': 'Taetigkeit & Beruf',
  'privatesLeben': 'Privates Leben',
  'stilWirkung': 'Stil & Wirkung',
  'ressourcenMittel': 'Ressourcen & Mittel',
  'gesundheit': 'Gesundheit'
};

/**
 * Big Five Personality Dimension
 */
interface BigFiveDimensionScore {
  dimension: 'O' | 'C' | 'E' | 'A' | 'N';
  label: string;
  score: number;
  description: string;
}

/**
 * RIASEC Score Data
 */
interface RiasecData {
  scores?: Record<string, number>;
  hollandCode?: string;
  hollandType?: string;
}

/**
 * Motivation Dimension Score
 */
interface MotiveDimensionScore {
  id: string;
  label: string;
  score?: number;
  averageScore?: number;
}

/**
 * Valou Area Entry with mood and preferences
 */
interface ValouAreaEntry {
  mood?: number;
  vorlieben?: string[];
  abneigungen?: string[];
  mustHaves?: string[];
  noGos?: string[];
  stylingSatz?: string;
}

/**
 * Logbook Entry
 */
interface LogbookEntry {
  date: string;
  wellbeing?: number;
  energyLevel?: number;
  mentalLoad?: number;
  goodWorkIndex?: number;
}

/**
 * Importable data categories for UI selection
 */
export interface ImportableData {
  name: string | null;
  personality: {
    bigFiveScores: BigFiveDimensionScore[] | null;
    summary: string | null;
  } | null;
  riasec: {
    hollandCode: string | null;
    hollandType: string | null;
    scores: Record<string, number> | null;
  } | null;
  motivation: {
    topMotives: string[];
    scores: MotiveDimensionScore[] | null;
  } | null;
  futureSkills: {
    topSkills: string[];
  } | null;
  valouPreferences: {
    vorlieben: string[];
    abneigungen: string[];
    mustHaves: string[];
    noGos: string[];
  } | null;
  valouMoods: Record<string, number> | null;
  mentalLoadScores: Record<string, number> | null;
  averageMentalLoad: number | null;
  averageValouMood: number | null;
  currentWellbeing: number | null;
  currentEnergyLevel: number | null;
}

/**
 * Extract Valou preferences from valouZielstylingData
 * Combines all life areas
 */
function extractValouPreferences(valouData: Record<string, ValouAreaEntry> | undefined): ImportableData['valouPreferences'] {
  if (!valouData) return null;

  const vorlieben: string[] = [];
  const abneigungen: string[] = [];
  const mustHaves: string[] = [];
  const noGos: string[] = [];

  // Iterate through all life areas
  Object.values(valouData).forEach((areaData) => {
    if (areaData && typeof areaData === 'object') {
      if (Array.isArray(areaData.vorlieben)) {
        vorlieben.push(...areaData.vorlieben.filter((v): v is string => typeof v === 'string'));
      }
      if (Array.isArray(areaData.abneigungen)) {
        abneigungen.push(...areaData.abneigungen.filter((v): v is string => typeof v === 'string'));
      }
      if (Array.isArray(areaData.mustHaves)) {
        mustHaves.push(...areaData.mustHaves.filter((v): v is string => typeof v === 'string'));
      }
      if (Array.isArray(areaData.noGos)) {
        noGos.push(...areaData.noGos.filter((v): v is string => typeof v === 'string'));
      }
    }
  });

  // Only return if we have any data
  if (vorlieben.length === 0 && abneigungen.length === 0 && mustHaves.length === 0 && noGos.length === 0) {
    return null;
  }

  // Remove duplicates
  return {
    vorlieben: [...new Set(vorlieben)],
    abneigungen: [...new Set(abneigungen)],
    mustHaves: [...new Set(mustHaves)],
    noGos: [...new Set(noGos)],
  };
}

/**
 * Extract Valou moods from valouZielstylingData
 */
function extractValouMoods(valouData: Record<string, ValouAreaEntry> | undefined): Record<string, number> | null {
  if (!valouData) {
    console.log('[CraftingIntegration] extractValouMoods: No valouData provided');
    return null;
  }

  const moods: Record<string, number> = {};
  let hasAnyMood = false;

  VALOU_AREA_IDS.forEach((areaId) => {
    const areaData = valouData[areaId];
    console.log(`[CraftingIntegration] Checking mood for ${areaId}:`, {
      hasAreaData: !!areaData,
      moodValue: areaData?.mood,
      moodType: typeof areaData?.mood
    });
    if (areaData?.mood !== undefined && typeof areaData.mood === 'number') {
      moods[areaId] = areaData.mood;
      hasAnyMood = true;
    }
  });

  console.log('[CraftingIntegration] extractValouMoods result:', { hasAnyMood, moods });
  return hasAnyMood ? moods : null;
}

/**
 * Check if a GoodWork Crafting profile exists for the user
 * Checks primary collection first, then fallback to legacy
 */
export async function hasCraftingProfile(userId: string): Promise<boolean> {
  try {
    // Try primary collection first
    let docRef = doc(db, CRAFTING_USERS_COLLECTION, userId);
    let docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return true;
    }

    // Fallback to legacy collection
    docRef = doc(db, CRAFTING_LEGACY_COLLECTION, userId);
    docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch {
    return false;
  }
}

/**
 * Get all importable data from GoodWork Crafting profile
 * Checks primary collection first, then fallback to legacy
 */
export async function getImportableData(userId: string): Promise<ImportableData | null> {
  try {
    // Try primary collection first
    let docRef = doc(db, CRAFTING_USERS_COLLECTION, userId);
    let docSnap = await getDoc(docRef);
    let usedCollection = CRAFTING_USERS_COLLECTION;

    // Fallback to legacy collection if not found
    if (!docSnap.exists()) {
      console.log('[CraftingIntegration] Primary collection empty, trying legacy...');
      docRef = doc(db, CRAFTING_LEGACY_COLLECTION, userId);
      docSnap = await getDoc(docRef);
      usedCollection = CRAFTING_LEGACY_COLLECTION;
    }

    if (!docSnap.exists()) {
      console.log('[CraftingIntegration] No data found in either collection');
      return null;
    }

    const data = docSnap.data();

    // DEBUG: Log what we got from Firestore
    console.log('[CraftingIntegration] Loaded from collection:', usedCollection);
    console.log('[CraftingIntegration] Raw valouZielstylingData:', JSON.stringify(data.valouZielstylingData, null, 2));
    console.log('[CraftingIntegration] Has personalityScreening:', !!data.personalityScreening);
    console.log('[CraftingIntegration] Has logbookEntries:', !!data.logbookEntries, 'count:', data.logbookEntries?.length);

    // 1. Name - from "Mein Name" field
    const name = typeof data.name === 'string' && data.name.trim() !== '' ? data.name : null;

    // 2. Personality Screening (Big Five)
    const personalityScreening = data.personalityScreening as Record<string, unknown> | undefined;
    let personality: ImportableData['personality'] = null;

    if (personalityScreening) {
      const bigFiveScores = personalityScreening.bigFiveScores as BigFiveDimensionScore[] | undefined;
      const report = personalityScreening.report as string | undefined;

      if (bigFiveScores && bigFiveScores.length > 0) {
        personality = {
          bigFiveScores,
          summary: report || null
        };
      }
    }

    // 3. RIASEC Data
    const riasecData = data.riasec as RiasecData | undefined;
    let riasec: ImportableData['riasec'] = null;

    if (riasecData) {
      riasec = {
        hollandCode: riasecData.hollandCode || null,
        hollandType: riasecData.hollandType || null,
        scores: riasecData.scores || null
      };
    }

    // 4. Motivation Screening
    const motivationScreening = data.motivationScreening as Record<string, unknown> | undefined;
    let motivation: ImportableData['motivation'] = null;

    if (motivationScreening) {
      const dimensionScores = motivationScreening.dimensionScores as MotiveDimensionScore[] | undefined;
      const topMotives: string[] = [];

      if (dimensionScores && Array.isArray(dimensionScores)) {
        // Get top 3 motives by score
        const sorted = [...dimensionScores].sort((a, b) =>
          ((b.score || b.averageScore) || 0) - ((a.score || a.averageScore) || 0)
        );
        topMotives.push(...sorted.slice(0, 3).map(m => m.label));

        motivation = {
          topMotives,
          scores: dimensionScores
        };
      }
    }

    // 5. Future Skills Screening
    const futureSkillsScreening = data.futureSkillsScreening as Record<string, unknown> | undefined;
    let futureSkills: ImportableData['futureSkills'] = null;

    if (futureSkillsScreening) {
      const skills = futureSkillsScreening.topSkills as string[] | undefined;
      if (skills && skills.length > 0) {
        futureSkills = {
          topSkills: skills
        };
      }
    }

    // 6. Valou Styling Data
    const valouZielstylingData = data.valouZielstylingData as Record<string, ValouAreaEntry> | undefined;
    const valouPreferences = extractValouPreferences(valouZielstylingData);
    const valouMoods = extractValouMoods(valouZielstylingData);

    // 7. Mental Load from Feel Better Tool
    const feelBetterTool = data.feelBetterTool as Record<string, unknown> | undefined;
    let mentalLoadScores: Record<string, number> | null = null;
    let averageMentalLoad: number | null = null;

    if (feelBetterTool?.energyManagement) {
      const energyMgmt = feelBetterTool.energyManagement as Record<string, unknown>;
      const scores = energyMgmt.mentalLoadScores as Record<string, number> | undefined;

      if (scores && Object.keys(scores).length > 0) {
        mentalLoadScores = scores;
        const values = Object.values(scores);
        averageMentalLoad = Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
      }
    }

    // 8. Current wellbeing from persistentWellbeing (the "Wetter" setting in GoodWork Daily)
    // This is the primary source - it's directly on the profile, not in logbook entries
    let currentWellbeing: number | null = null;
    let currentEnergyLevel: number | null = null;

    // Primary: persistentWellbeing (the weather/wellbeing slider in GoodWork Daily)
    if (typeof data.persistentWellbeing === 'number') {
      currentWellbeing = data.persistentWellbeing;
      console.log('[CraftingIntegration] Found persistentWellbeing:', currentWellbeing);
    }

    // Fallback: check logbook entries if persistentWellbeing not available
    if (currentWellbeing === null) {
      const logbookEntries = data.logbookEntries as (LogbookEntry & { weatherMood?: number })[] | undefined;
      if (logbookEntries && logbookEntries.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const todayEntry = logbookEntries.find(entry => entry.date === today);

        if (todayEntry) {
          currentWellbeing = todayEntry.wellbeing ?? todayEntry.weatherMood ?? null;
          currentEnergyLevel = todayEntry.energyLevel ?? null;
        }

        if (currentWellbeing === null) {
          const sorted = [...logbookEntries].sort((a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          const latest = sorted[0];
          currentWellbeing = latest.wellbeing ?? latest.weatherMood ?? null;
          currentEnergyLevel = latest.energyLevel ?? null;
        }
      }
    }

    // 9. Calculate average Valou mood from individual moods
    let averageValouMood: number | null = null;
    if (valouMoods && Object.keys(valouMoods).length > 0) {
      const moodValues = Object.values(valouMoods);
      averageValouMood = Math.round((moodValues.reduce((sum, v) => sum + v, 0) / moodValues.length) * 10) / 10;
    }

    // DEBUG: Log extracted values
    console.log('[CraftingIntegration] Extracted data:', {
      name,
      currentWellbeing,
      currentEnergyLevel,
      averageValouMood,
      averageMentalLoad,
      valouMoodsCount: valouMoods ? Object.keys(valouMoods).length : 0
    });

    return {
      name,
      personality,
      riasec,
      motivation,
      futureSkills,
      valouPreferences,
      valouMoods,
      mentalLoadScores,
      averageMentalLoad,
      averageValouMood,
      currentWellbeing,
      currentEnergyLevel
    };
  } catch (error) {
    console.error('Error getting crafting data:', error);
    return null;
  }
}

/**
 * Convert selected ImportableData to CraftingData for storage
 */
export function toCraftingData(data: ImportableData, selections: {
  personality?: boolean;
  motivation?: boolean;
  riasec?: boolean;
  valou?: boolean;
}): CraftingData {
  const craftingData: CraftingData = {
    lastSyncedAt: Timestamp.now(),
  };

  if (selections.personality && data.personality) {
    // Store Big Five summary
    const bigFive = data.personality.bigFiveScores;
    if (bigFive && bigFive.length > 0) {
      // Create a personality type string from Big Five
      const dominant = bigFive.reduce((a, b) => a.score > b.score ? a : b);
      craftingData.personalityType = `${dominant.label} (Big Five)`;
    }
  }

  if (selections.motivation && data.motivation) {
    craftingData.motivationFactors = data.motivation.topMotives.length > 0
      ? data.motivation.topMotives
      : undefined;
  }

  if (selections.valou && data.valouPreferences) {
    craftingData.valouPreferences = data.valouPreferences;
  }

  // Add RIASEC type
  if (selections.riasec && data.riasec?.hollandType) {
    craftingData.riasecType = data.riasec.hollandType;
  }

  // Add mood and mental load data
  if (data.valouMoods) {
    craftingData.valouMoods = data.valouMoods;
  }

  if (data.averageMentalLoad !== null) {
    craftingData.averageMentalLoad = data.averageMentalLoad;
  }

  if (data.currentWellbeing !== null) {
    craftingData.currentWellbeing = data.currentWellbeing;
  }

  return craftingData;
}

/**
 * Generate interests from selected data categories
 */
export function generateInterests(data: ImportableData, selections: {
  skills?: boolean;
  values?: boolean;
  strengths?: boolean;
}): string[] {
  const interests: string[] = [];

  // Add top skills from future skills screening
  if (selections.skills && data.futureSkills?.topSkills) {
    interests.push(...data.futureSkills.topSkills);
  }

  // Add vorlieben as interests
  if (selections.values && data.valouPreferences?.vorlieben) {
    interests.push(...data.valouPreferences.vorlieben.slice(0, 10));
  }

  // Remove duplicates and limit to 20 items
  return [...new Set(interests)].slice(0, 20);
}

/**
 * Get a summary of available data for display
 */
export function getDataSummary(data: ImportableData): {
  hasName: boolean;
  hasPersonality: boolean;
  hasMotivation: boolean;
  hasRiasec: boolean;
  hasFutureSkills: boolean;
  hasValou: boolean;
  hasValouMoods: boolean;
  hasMentalLoad: boolean;
  hasWellbeing: boolean;
} {
  return {
    hasName: !!data.name,
    hasPersonality: !!data.personality?.bigFiveScores,
    hasMotivation: !!(data.motivation && data.motivation.topMotives.length > 0),
    hasRiasec: !!data.riasec?.hollandCode,
    hasFutureSkills: !!(data.futureSkills && data.futureSkills.topSkills.length > 0),
    hasValou: !!data.valouPreferences,
    hasValouMoods: !!data.valouMoods,
    hasMentalLoad: data.averageMentalLoad !== null,
    hasWellbeing: data.currentWellbeing !== null
  };
}

/**
 * Generate a personalized place recommendation based on Crafting data
 */
export function generatePersonalizedRecommendation(data: ImportableData): {
  message: string;
  suggestedCategories: string[];
  suggestedMood: string | null;
  priority: 'relax' | 'energize' | 'focus' | 'social' | 'explore';
} {
  const suggestedCategories: string[] = [];
  let priority: 'relax' | 'energize' | 'focus' | 'social' | 'explore' = 'explore';
  let message = '';
  let suggestedMood: string | null = null;

  // Analyze mental load and wellbeing
  const mentalLoad = data.averageMentalLoad;
  const wellbeing = data.currentWellbeing;
  const valouMoods = data.valouMoods;

  // Find areas that need attention (mood < 5)
  const lowMoodAreas: string[] = [];
  if (valouMoods) {
    Object.entries(valouMoods).forEach(([areaId, mood]) => {
      if (mood < 5) {
        lowMoodAreas.push(VALOU_AREA_NAMES[areaId] || areaId);
      }
    });
  }

  // High mental load (>70) - suggest relaxation
  if (mentalLoad !== null && mentalLoad > 70) {
    priority = 'relax';
    message = 'Dein Mental Load ist gerade erhoeht. Zeit fuer eine Auszeit!';
    suggestedCategories.push('Spa & Wellness', 'Natur', 'Ruhige Cafes');
    suggestedMood = 'calm';
  }
  // Low wellbeing (<5) - suggest energizing activities
  else if (wellbeing !== null && wellbeing < 5) {
    priority = 'energize';
    message = 'Gib dir selbst einen Energieschub! Diese Orte koennten deine Stimmung heben.';
    suggestedCategories.push('Sport & Bewegung', 'Natur', 'Kreativraum');
    suggestedMood = 'lively';
  }
  // Low mood in specific areas
  else if (lowMoodAreas.length > 0) {
    if (lowMoodAreas.includes('Gesundheit')) {
      priority = 'relax';
      message = 'Dein Gesundheits-Valou braucht Aufmerksamkeit. Goenne dir etwas Gutes!';
      suggestedCategories.push('Spa & Wellness', 'Bio-Restaurants', 'Yoga-Studios');
      suggestedMood = 'calm';
    } else if (lowMoodAreas.includes('Privates Leben')) {
      priority = 'social';
      message = 'Zeit fuer soziale Kontakte! Triff Freunde an einem schoenen Ort.';
      suggestedCategories.push('Cafes', 'Bars', 'Restaurants');
      suggestedMood = 'social';
    } else if (lowMoodAreas.includes('Taetigkeit & Beruf')) {
      priority = 'focus';
      message = 'Brauchst du einen Tapetenwechsel zum Arbeiten? Probiere diese Orte!';
      suggestedCategories.push('Coworking', 'Bibliotheken', 'Ruhige Cafes');
      suggestedMood = 'creative';
    } else {
      priority = 'explore';
      message = `Dein ${lowMoodAreas[0]}-Bereich koennte etwas Abwechslung gebrauchen.`;
      suggestedCategories.push('Kultur', 'Events', 'Neue Orte');
      suggestedMood = 'surprise';
    }
  }
  // Check personality for suggestions
  else if (data.personality?.bigFiveScores) {
    const bigFive = data.personality.bigFiveScores;
    const extraversion = bigFive.find(b => b.dimension === 'E');
    const openness = bigFive.find(b => b.dimension === 'O');

    if (extraversion && extraversion.score > 7) {
      priority = 'social';
      message = 'Als extrovertierter Mensch tankst du Energie bei anderen. Hier sind lebhafte Orte!';
      suggestedCategories.push('Bars', 'Events', 'Coworking');
      suggestedMood = 'lively';
    } else if (extraversion && extraversion.score < 4) {
      priority = 'relax';
      message = 'Ruhige Orte sind dein Ding. Hier kannst du entspannen und auftanken.';
      suggestedCategories.push('Bibliotheken', 'Ruhige Cafes', 'Natur');
      suggestedMood = 'calm';
    } else if (openness && openness.score > 7) {
      priority = 'explore';
      message = 'Du liebst neue Erfahrungen! Entdecke kreative und ungewoehnliche Orte.';
      suggestedCategories.push('Galerien', 'Kultur', 'Pop-ups');
      suggestedMood = 'creative';
    }
  }
  // Check RIASEC type
  else if (data.riasec?.hollandType) {
    const type = data.riasec.hollandType.toLowerCase();
    if (type.includes('social')) {
      priority = 'social';
      message = 'Dein RIASEC-Typ zeigt: Du liebst Menschen! Hier triffst du Gleichgesinnte.';
      suggestedCategories.push('Community Spaces', 'Cafes', 'Events');
      suggestedMood = 'social';
    } else if (type.includes('artistic') || type.includes('investigative')) {
      priority = 'focus';
      message = 'Kreative und inspirierende Orte passen zu deinem Profil.';
      suggestedCategories.push('Ateliers', 'Makerspaces', 'Bibliotheken');
      suggestedMood = 'creative';
    }
  }
  // Default recommendation based on preferences
  else if (data.valouPreferences?.vorlieben && data.valouPreferences.vorlieben.length > 0) {
    const topPrefs = data.valouPreferences.vorlieben.slice(0, 3).join(', ');
    priority = 'explore';
    message = `Basierend auf deinen Vorlieben (${topPrefs}) - hier sind passende Orte!`;
    suggestedMood = 'surprise';
  }
  // Fallback
  else {
    message = 'Entdecke neue Orte, die zu dir passen koennten!';
    priority = 'explore';
    suggestedMood = 'surprise';
  }

  return {
    message,
    suggestedCategories,
    suggestedMood,
    priority
  };
}
